import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Twitch, Youtube, Instagram, Twitter } from 'lucide-react';

const Streamers = () => {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const streamers = [
    {
      id: 1,
      name: 'Zangado',
      photo: '/streamers/zangado.jpg',
      role: 'Streamer',
      description: 'Gameplay, PvP e eventos',
      socials: {
        twitch: 'https://www.twitch.tv/zangadogamers',
        youtube: 'https://www.youtube.com/@ZANGADOGAMER',
      }
    },
    {
      id: 2,
      name: 'Mafusa',
      photo: '/streamers/mafu.jpeg',
      role: 'Streamer',
      description: 'Gameplay, PvP e eventos',
      socials: {
        twitch: 'https://www.twitch.tv/mafubpt',
        instagram: 'https://www.instagram.com/gmssilveira/'
      }
    },
    {
      id: 3,
      name: 'Cejay',
      photo: '/streamers/cj.jpeg',
      role: 'Streamer',
      description: 'Gameplay, PvP e eventos',
      socials: {
        twitch: 'https://www.twitch.tv/cejaydk',
        youtube: 'https://www.youtube.com/@CejaYy',
      }
    },
    {
      id: 4,
      name: 'Sabaraloka',
      photo: '/streamers/sabara.jpg',
      role: 'Streamer',
      description: 'Gameplay, PvP e eventos',
      socials: {
        youtube: 'https://www.youtube.com/@SABARALOKA',
      }
    },
  ];

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section ref={ref} className="py-20 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-400 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-2 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Assista aos Melhores
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Acompanhe nossos streamers suas jornadas
          </p>
        </motion.div>

        <div className="relative">
          {/* Scroll indicator for mobile */}
          <div className="lg:hidden text-center mb-4">
            <p className="text-blue-300 text-sm animate-pulse">
              👈 Deslize para ver mais →
            </p>
          </div>

          <div className="flex overflow-x-auto gap-6 pb-6 snap-x snap-mandatory scrollbar-hide lg:grid lg:grid-cols-5 lg:overflow-visible">
            {streamers.map((streamer) => (
              <div key={streamer.id} className="px-2">
                <motion.div
                  variants={itemVariants}
                  className="group h-full w-min[300px]"
                >
                  <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-5 sm:p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 shadow-xl h-full flex flex-col w-full">
                    {/* Photo */}
                    <div className="relative mb-4 sm:mb-6 overflow-hidden rounded-xl">
                      <div className="aspect-square bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                        {!streamer.photo ? (
                          // Placeholder se a foto não existir
                          <div className="text-4xl sm:text-6xl">🎮</div>
                        ) : (
                          <img
                            src={streamer.photo}
                            alt={streamer.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-purple-900/90 via-purple-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                        <span className="text-white font-bold text-sm sm:text-lg">Ao Vivo 🔴</span>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="text-center mb-3 sm:mb-4 flex-grow">
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2 break-words">
                        {streamer.name}
                      </h3>
                      <div className="inline-block bg-purple-500/20 px-2 sm:px-3 py-1 rounded-full mb-2">
                        <span className="text-purple-300 text-xs sm:text-sm font-semibold">
                          {streamer.role}
                        </span>
                      </div>
                      <p className="text-gray-400 text-xs sm:text-sm line-clamp-2">
                        {streamer.description}
                      </p>
                    </div>

                    {/* Social Links */}
                    <div className="flex items-center justify-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-white/10">
                      {streamer.socials.twitch && (
                        <a
                          href={streamer.socials.twitch}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-600/20 hover:bg-purple-600 rounded-lg flex items-center justify-center transition-all duration-300 group/icon"
                          aria-label="Twitch"
                        >
                          <Twitch className="w-4 h-4 sm:w-5 sm:h-5 text-purple-300 group-hover/icon:text-white transition-colors" />
                        </a>
                      )}
                      {streamer.socials.youtube && (
                        <a
                          href={streamer.socials.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-9 h-9 sm:w-10 sm:h-10 bg-red-600/20 hover:bg-red-600 rounded-lg flex items-center justify-center transition-all duration-300 group/icon"
                          aria-label="YouTube"
                        >
                          <Youtube className="w-4 h-4 sm:w-5 sm:h-5 text-red-300 group-hover/icon:text-white transition-colors" />
                        </a>
                      )}
                      {streamer.socials.instagram && (
                        <a
                          href={streamer.socials.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-9 h-9 sm:w-10 sm:h-10 bg-pink-600/20 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600 rounded-lg flex items-center justify-center transition-all duration-300 group/icon"
                          aria-label="Instagram"
                        >
                          <Instagram className="w-4 h-4 sm:w-5 sm:h-5 text-pink-300 group-hover/icon:text-white transition-colors" />
                        </a>
                      )}
                      {streamer.socials.twitter && (
                        <a
                          href={streamer.socials.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-600/20 hover:bg-blue-500 rounded-lg flex items-center justify-center transition-all duration-300 group/icon"
                          aria-label="Twitter"
                        >
                          <Twitter className="w-4 h-4 sm:w-5 sm:h-5 text-blue-300 group-hover/icon:text-white transition-colors" />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Streamers;
